import { useEffect } from "react";
import Grid from "@mui/material/Grid";
import CardContent from "@mui/material/CardContent";
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import Button from '@mui/material/Button';
import { Link } from 'react-router-dom';
import useMediaQuery from '@mui/material/useMediaQuery';

// Icons
import LaptopIcon from '@mui/icons-material/Laptop';
import CloudIcon from '@mui/icons-material/Cloud';
import GroupsIcon from '@mui/icons-material/Groups';
import SecurityIcon from '@mui/icons-material/Security';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import WarningIcon from '@mui/icons-material/Warning';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ShieldIcon from '@mui/icons-material/Shield';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SpeedIcon from '@mui/icons-material/Speed';

// Images
import computerUser from 'assets/images/photos/computer-user.jpg';
import noc from 'assets/images/photos/noc.jpg';
import decisionTree from 'assets/images/illustrations/decision-tree.png';

export default function AboutUs() {
    const isMobile = useMediaQuery((theme) => theme.breakpoints.down('md'));

    useEffect(() => {
        document.title = 'About PREVAIL — Protecting Small Businesses from Cyber Threats';
        window.scrollTo(0, 0);
    });

    return (
        <>
            {/* ─── HERO ─── */}
            <Box
                width="100%"
                sx={{
                    minHeight: isMobile ? '100vh' : '72vh',
                    background: 'linear-gradient(160deg, #000000 0%, #1a0000 50%, #242331 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                <Grid container maxWidth="lg" spacing={2} p={3} mt={8} justifyContent="center" alignItems="center">
                    <Grid item xs={12} md={7} p={2}>
                        <Typography
                            sx={{ typography: { xs: 'h4', md: 'h2' }, mb: 2 }}
                            fontWeight="bold"
                            color="grey.100"
                        >
                            We Find What Others Fail to Prevent.
                        </Typography>
                        <Typography
                            sx={{ typography: { xs: 'h6', md: 'h5' }, mb: 1 }}
                            color="prevailRed.main"
                            fontWeight="bold"
                        >
                            Enterprise-Grade AI Security for Businesses That Can't Afford a Security Team.
                        </Typography>
                        <Typography
                            variant="h6"
                            color="grey.400"
                            sx={{ mb: 4, mt: 2, lineHeight: 1.8 }}
                        >
                            PREVAIL was built for one reason: small and medium businesses are the most
                            frequently targeted, the least protected, and the most devastated by
                            cyberattacks. We exist to change that.
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                            <Link to="https://app.prevailcyber.com/sign-up" target="_blank">
                                <Button
                                    variant="contained"
                                    color="prevailRed"
                                    size="large"
                                    sx={{ pl: 5, pr: 5, py: 1.5 }}
                                >
                                    <Typography sx={{ fontSize: '1.1em' }} color="grey.100">
                                        Start Free Trial
                                    </Typography>
                                </Button>
                            </Link>
                            <Link to="/#contact">
                                <Button
                                    variant="outlined"
                                    size="large"
                                    sx={{ pl: 4, pr: 4, py: 1.5, borderColor: 'grey.600', color: 'grey.300' }}
                                >
                                    <Typography sx={{ fontSize: '1.1em' }}>Contact Us</Typography>
                                </Button>
                            </Link>
                        </Box>
                    </Grid>

                </Grid>
            </Box>

            {/* ─── STATS BAR ─── */}
            <Box sx={{ background: '#6b0000', py: 4 }}>
                <Grid container maxWidth="xl" spacing={2} justifyContent="center" alignItems="center"
                    textAlign="center" sx={{ mx: 'auto' }}>
                    {[
                        { stat: '43%', label: 'Of all cyberattacks target small businesses' },
                        { stat: '60%', label: 'Of SMBs close within 6 months of a breach' },
                        { stat: '< 1 Min', label: 'PREVAIL breach detection time' },
                        { stat: '$2', label: 'Per user/month for full protection' },
                    ].map(({ stat, label }) => (
                        <Grid item size={{ xs: 6, md: 3 }} key={stat}>
                            <Typography variant="h4" color="grey.100" fontWeight="bold">{stat}</Typography>
                            <Typography variant="body2" color="grey.400" mt={0.5}>{label}</Typography>
                        </Grid>
                    ))}
                </Grid>
            </Box>

            {/* ─── THE WORLD HAS CHANGED ─── */}
            <Box
                sx={{
                    background: '#242331',
                    py: 10,
                    display: 'grid',
                    placeItems: 'center',
                }}
            >
                <Grid container maxWidth="lg" spacing={2} justifyContent="center">
                    <Grid item xs={12} textAlign="center" mb={4}>
                        <Typography variant="h3" color="grey.100" fontWeight="bold">
                            The Way We Work Changed Everything
                        </Typography>
                        <Typography variant="h6" color="grey.400" mt={2} sx={{ maxWidth: '750px', mx: 'auto' }}>
                            Remote work, cloud adoption, and AI-powered attacks have fundamentally shifted
                            the cybersecurity landscape — and small businesses are caught in the middle.
                        </Typography>
                    </Grid>

                    {[
                        {
                            icon: <LaptopIcon sx={{ fontSize: 48 }} />,
                            title: 'Remote Work Erased the Perimeter',
                            description:
                                'When employees work from anywhere, there is no longer a corporate firewall to hide behind. Every laptop, every home network, every coffee shop WiFi is a potential entry point. The security perimeter is now the cloud account itself.',
                        },
                        {
                            icon: <CloudIcon sx={{ fontSize: 48 }} />,
                            title: 'Everything Lives in the Cloud',
                            description:
                                'Microsoft 365, Google Workspace, and cloud storage now hold your most valuable assets — client data, financial records, intellectual property, and communications. Attackers go where the data is. And the data is in the cloud.',
                        },
                        {
                            icon: <AutoAwesomeIcon sx={{ fontSize: 48 }} />,
                            title: 'AI Is Making Attacks Smarter',
                            description:
                                'Generative AI has transformed phishing from clumsy emails with poor grammar into perfectly crafted, highly personalized messages indistinguishable from legitimate communication. New MFA bypass techniques emerge constantly. The threat is evolving faster than traditional defenses.',
                        },
                    ].map(({ icon, title, description }) => (
                        <Grid item xs={12} md={4} key={title} p={1}>
                            <Box
                                sx={{
                                    background: '#1a1929',
                                    border: '1px solid rgba(217,0,0,0.35)',
                                    borderRadius: 2,
                                    p: 4,
                                    height: '100%',
                                }}
                            >
                                <Typography color="prevailRed.main" mb={2}>{icon}</Typography>
                                <Typography variant="h5" color="grey.100" fontWeight="bold" mb={2}>
                                    {title}
                                </Typography>
                                <Typography variant="body2" color="grey.400" lineHeight={1.8}>
                                    {description}
                                </Typography>
                            </Box>
                        </Grid>
                    ))}
                </Grid>
            </Box>

            {/* ─── WHY SMBS ARE THE TARGET ─── */}
            <Box
                sx={{
                    background: '#f8f9fa',
                    py: 10,
                    display: 'grid',
                    placeItems: 'center',
                }}
            >
                <Grid container maxWidth="lg" spacing={6} justifyContent="center" alignItems="center">
                    <Grid item xs={12} md={6}>
                        <Typography
                            variant="overline"
                            color="prevailRed.main"
                            fontWeight="bold"
                            sx={{ letterSpacing: 3, fontSize: '0.9em' }}
                        >
                            THE PROBLEM
                        </Typography>
                        <Typography variant="h3" color="grey.800" fontWeight="bold" mt={1} mb={3}>
                            Small Businesses Are the<br />
                            <Typography component="span" variant="h3" color="prevailRed.main" fontWeight="bold">
                                #1 Target.
                            </Typography>
                        </Typography>
                        <Typography variant="body1" color="grey.700" mb={3} sx={{ fontSize: '1.1em', lineHeight: 1.8 }}>
                            Large corporations have dedicated security operations centers, full-time analysts,
                            and million-dollar security stacks. When attackers hit them, they often fail.
                            So attackers go after easier prey.
                        </Typography>
                        <Typography variant="body1" color="grey.700" mb={4} sx={{ fontSize: '1.1em', lineHeight: 1.8 }}>
                            Small and medium businesses are ideal targets: valuable data, minimal defenses,
                            and employees without security training. The Microsoft 365 account of a 20-person
                            law firm or medical practice is just as valuable to an attacker as a Fortune 500 —
                            and far easier to compromise.
                        </Typography>
                        {[
                            '43% of all cyberattacks are directed at small businesses',
                            'Only 14% of SMBs rate their ability to mitigate threats as highly effective',
                            '94% of companies feel built-in MS365 security is insufficient',
                            'The average small business breach costs $200,000 — enough to close most firms',
                        ].map((point) => (
                            <Box key={point} display="flex" alignItems="flex-start" gap={1.5} mb={1.5}>
                                <WarningIcon sx={{ color: '#DB9D47', mt: 0.3, fontSize: 20, flexShrink: 0 }} />
                                <Typography variant="body2" color="grey.700" sx={{ fontSize: '1em' }}>{point}</Typography>
                            </Box>
                        ))}
                    </Grid>
                    <Grid item xs={12} md={5} sx={{ display: { xs: 'none', md: 'block' } }}>
                        <Box
                            sx={{
                                backgroundImage: `url(${noc})`,
                                backgroundSize: 'cover',
                                backgroundPosition: 'center',
                                height: '440px',
                                borderRadius: 2,
                                boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
                            }}
                        />
                    </Grid>
                </Grid>
            </Box>

            {/* ─── OUR ORIGIN ─── */}
            <Box
                sx={{
                    background: '#6d0000',
                    py: 10,
                    display: 'grid',
                    placeItems: 'center',
                }}
            >
                <Grid container maxWidth="lg" spacing={6} justifyContent="center" alignItems="center">
                    <Grid item xs={12} md={6} order={{ xs: 2, md: 1 }}>
                        <Typography
                            variant="overline"
                            color="prevailRed.main"
                            fontWeight="bold"
                            sx={{ letterSpacing: 3, fontSize: '0.9em' }}
                        >
                            OUR STORY
                        </Typography>
                        <Typography variant="h3" color="grey.100" fontWeight="bold" mt={1} mb={3}>
                            Built From a Gap No One Was Filling
                        </Typography>
                        <Typography variant="body1" color="grey.300" mb={3} sx={{ fontSize: '1.1em', lineHeight: 1.8 }}>
                            PREVAIL began as an agent-based data center security system — detecting
                            anomalous behavior across infrastructure services before attackers could
                            cause damage. The core idea was simple: stop relying on known attack
                            signatures, and start modeling what normal looks like instead.
                        </Typography>
                        <Typography variant="body1" color="grey.300" mb={3} sx={{ fontSize: '1.1em', lineHeight: 1.8 }}>
                            When the pandemic accelerated the shift to cloud services like Microsoft 365
                            and Google Workspace, we saw something alarming: millions of small businesses
                            were migrating their entire operations to the cloud with almost no security
                            protection. Attackers noticed immediately.
                        </Typography>
                        <Typography variant="body1" color="grey.300" sx={{ fontSize: '1.1em', lineHeight: 1.8 }}>
                            We pivoted to focus entirely on that problem — bringing behavioral AI threat
                            detection to the businesses that need it most but have always been priced out
                            of enterprise security. The result is PREVAIL: automated, intelligent, and
                            accessible to any business in any industry.
                        </Typography>
                    </Grid>
                   
                </Grid>
            </Box>

            {/* ─── THE GAP PREVAIL FILLS ─── */}
            <Box
                sx={{
                    background: '#242331',
                    py: 10,
                    display: 'grid',
                    placeItems: 'center',
                }}
            >
                <Grid container maxWidth="lg" spacing={4} justifyContent="center">
                    <Grid item xs={12} textAlign="center" mb={4}>
                        <Typography variant="h3" color="grey.100" fontWeight="bold">
                            The Security Gap PREVAIL Was Built to Close
                        </Typography>
                        <Typography variant="h6" color="grey.400" mt={2} sx={{ maxWidth: '720px', mx: 'auto' }}>
                            For decades, advanced security has been the exclusive domain of large enterprises
                            with massive budgets. PREVAIL changes that equation.
                        </Typography>
                    </Grid>

                    <Grid item size={{ xs: 12, md: 5}} >
                        <Box
                            sx={{
                                background: '#1a1929',
                                border: '2px solid rgba(255,255,255,0.1)',
                                borderRadius: 2,
                                p: 4,
                                height: '100%',
                            }}
                        >
                            <Typography variant="h5" color="grey.400" fontWeight="bold" mb={3}>
                                Before PREVAIL
                            </Typography>
                            {[
                                'Enterprise security costs $50,000–$500,000+ per year',
                                'Full-time analysts required to monitor and respond to alerts',
                                'SIEMs generate thousands of false positives requiring expert review',
                                'Small businesses use free tools that offer no behavioral detection',
                                'Breaches go undetected for nearly a year on average',
                                'When a breach is discovered, it\'s often from the attacker\'s ransom note',
                            ].map((point) => (
                                <Box key={point} display="flex" alignItems="flex-start" gap={1.5} mb={2}>
                                    <WarningIcon sx={{ color: '#DB9D47', mt: 0.2, fontSize: 20, flexShrink: 0 }} />
                                    <Typography variant="body2" color="grey.500" sx={{ fontSize: '0.95em' }}>{point}</Typography>
                                </Box>
                            ))}
                        </Box>
                    </Grid>

                    <Grid item size={{ xs: 12, md: 5}}>
                        <Box
                            sx={{
                                background: '#1a1929',
                                border: '2px solid #d90000',
                                borderRadius: 2,
                                p: 4,
                                height: '100%',
                            }}
                        >
                            <Typography variant="h5" color="prevailRed.main" fontWeight="bold" mb={3}>
                                With PREVAIL
                            </Typography>
                            {[
                                'Full protection starting at $2 per user per month',
                                'AI does the analysis — no security staff required',
                                'Behavioral baselines eliminate alert noise automatically',
                                'Any business protected in under 3 minutes — no IT needed',
                                'Active breaches detected and alerted in under 1 minute',
                                'Adapts to new attack methods as they emerge — no updates required',
                            ].map((point) => (
                                <Box key={point} display="flex" alignItems="flex-start" gap={1.5} mb={2}>
                                    <CheckCircleIcon sx={{ color: '#47A025', mt: 0.2, fontSize: 20, flexShrink: 0 }} />
                                    <Typography variant="body2" color="grey.300" sx={{ fontSize: '0.95em' }}>{point}</Typography>
                                </Box>
                            ))}
                        </Box>
                    </Grid>
                </Grid>
            </Box>

            {/* ─── OUR MISSION ─── */}
            <Box
                sx={{
                    background: '#f8f9fa',
                    py: 10,
                    display: 'grid',
                    placeItems: 'center',
                }}
            >
                <Grid container maxWidth="lg" spacing={4} justifyContent="center">
                    <Grid item xs={12} textAlign="center" mb={4}>
                        <Typography
                            variant="overline"
                            color="prevailRed.main"
                            fontWeight="bold"
                            sx={{ letterSpacing: 3, fontSize: '0.9em' }}
                        >
                            OUR MISSION
                        </Typography>
                        <Typography variant="h3" color="grey.800" fontWeight="bold" mt={1} mb={3}>
                            Accessible. Automated. Always On.
                        </Typography>
                        <Typography variant="h6" color="grey.600" sx={{ maxWidth: '700px', mx: 'auto', lineHeight: 1.8 }}>
                            We believe that no business should be left defenseless because they can't
                            afford a security operations center. PREVAIL's mission is to democratize
                            intelligent threat detection — making it as simple to activate as a
                            software subscription, and as powerful as any enterprise platform.
                        </Typography>
                    </Grid>

                    {[
                        {
                            icon: <ShieldIcon sx={{ fontSize: 48 }} />,
                            title: 'Protect the Unprotected',
                            description:
                                'Hundreds of thousands of small businesses run Microsoft 365 with no meaningful security beyond the defaults. PREVAIL gives every one of them the same behavioral AI that large enterprises rely on.',
                        },
                        {
                            icon: <SpeedIcon sx={{ fontSize: 48 }} />,
                            title: 'Reduce Dwell Time to Minutes',
                            description:
                                'The longer an attacker operates inside your systems, the more damage they cause. Our singular focus is reducing that time from months to under one minute — cutting off attacks before they escalate.',
                        },
                        {
                            icon: <VisibilityIcon sx={{ fontSize: 48 }} />,
                            title: 'Remove the Human Bottleneck',
                            description:
                                'Security systems that require human review to act are only as fast as the humans reviewing them. PREVAIL\'s fully automated detection eliminates the delay between threat and alert — no analyst needed.',
                        },
                        {
                            icon: <TrendingUpIcon sx={{ fontSize: 48 }} />,
                            title: 'Adapt Faster Than Attackers',
                            description:
                                'Signature-based security is always one step behind. By modeling behavior rather than matching known patterns, PREVAIL detects novel attacks automatically — even ones that have never been seen before.',
                        },
                        {
                            icon: <GroupsIcon sx={{ fontSize: 48 }} />,
                            title: 'Built for Real Businesses',
                            description:
                                'A law firm, a dental practice, an architecture studio, a medical research company — these are the organizations PREVAIL was built for. Real businesses with real data and no full-time IT security staff.',
                        },
                        {
                            icon: <SecurityIcon sx={{ fontSize: 48 }} />,
                            title: 'Security That Works for MSPs Too',
                            description:
                                'Managed Service Providers protecting dozens of clients need automation at scale. PREVAIL\'s multi-tenant platform lets MSPs deliver serious threat detection across every client from a single dashboard.',
                        },
                    ].map(({ icon, title, description }) => (
                        <Grid item xs={12} md={4} key={title}>
                            <Box
                                sx={{
                                    border: '2px solid',
                                    borderColor: 'grey.200',
                                    borderRadius: 2,
                                    p: 4,
                                    height: '100%',
                                    transition: 'border-color 0.3s',
                                    '&:hover': { borderColor: '#d90000' },
                                }}
                            >
                                <Typography color="prevailRed.main" mb={2}>{icon}</Typography>
                                <Typography variant="h5" color="grey.800" fontWeight="bold" mb={2}>{title}</Typography>
                                <Typography variant="body2" color="grey.600" lineHeight={1.8}>{description}</Typography>
                            </Box>
                        </Grid>
                    ))}
                </Grid>
            </Box>

            {/* ─── WHAT WE PROTECT AGAINST ─── */}
            <Box
                sx={{
                    background: '#242331',
                    py: 10,
                    display: 'grid',
                    placeItems: 'center',
                }}
            >
                <Grid container maxWidth="lg" spacing={4} justifyContent="center">
                    <Grid item xs={12} textAlign="center" mb={4}>
                        <Typography variant="h3" color="grey.100" fontWeight="bold">
                            The Threats We Were Built to Stop
                        </Typography>
                        <Typography variant="h6" color="grey.400" mt={2} sx={{ maxWidth: '700px', mx: 'auto' }}>
                            Every capability in PREVAIL was designed in response to real attack methods
                            used against real businesses — including our own customers.
                        </Typography>
                    </Grid>

                    {[
                        {
                            stat: '94%',
                            label: 'Of companies feel MS365 security is insufficient',
                            source: 'Mimecast State of Email Security',
                        },
                        {
                            stat: '328 Days',
                            label: 'Average time to identify a breach from stolen credentials',
                            source: 'IBM Cost of a Data Breach',
                        },
                        {
                            stat: '27%',
                            label: 'Of breaches are disclosed by attackers through ransomware demands',
                            source: 'IBM Cost of a Data Breach',
                        },
                        {
                            stat: '108 Days',
                            label: 'Faster breach detection with AI vs. no automation',
                            source: 'IBM Cost of a Data Breach',
                        },
                    ].map(({ stat, label, source }) => (
                        <Grid item xs={12} md={3} key={stat} textAlign="center">
                            <Box
                                sx={{
                                    background: '#DB9D47',
                                    borderRadius: 2,
                                    p: 4,
                                    border: '1px solid rgba(255,255,255,0.08)',
                                    height: '100%',
                                }}
                            >
                                <Typography variant="h3" color="grey.100" fontWeight="bold" mb={1}>{stat}</Typography>
                                <Typography variant="body2" color="grey.100" mb={2} lineHeight={1.7}>{label}</Typography>
                                <Divider sx={{ borderColor: 'rgba(255,255,255,0.15)', mb: 1.5 }} />
                                <Typography variant="caption" color="grey.200" sx={{ fontStyle: 'italic' }}>
                                    {source}
                                </Typography>
                            </Box>
                        </Grid>
                    ))}
                </Grid>
            </Box>

            {/* ─── FINAL CTA ─── */}
            <Box
                sx={{
                    background: 'linear-gradient(135deg, #2D0000 0%, #6b0000 60%, #242331 100%)',
                    py: 12,
                    display: 'grid',
                    placeItems: 'center',
                    textAlign: 'center',
                }}
            >
                <Grid container maxWidth="md" spacing={2} justifyContent="center">
                    <Grid item xs={12}>
                        <Typography variant="h3" color="grey.100" fontWeight="bold" mb={2}>
                            Join the Businesses PREVAIL Is Protecting
                        </Typography>
                        <Typography variant="h6" color="grey.400" mb={5} sx={{ maxWidth: '580px', mx: 'auto' }}>
                            You built your business. Don't let an attacker take it from you.
                            Get protected in under 3 minutes — no security team required.
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 3, justifyContent: 'center', flexWrap: 'wrap' }}>
                            <Link to="https://app.prevailcyber.com/sign-up" target="_blank">
                                <Button
                                    variant="contained"
                                    color="prevailRed"
                                    size="large"
                                    sx={{ pl: 6, pr: 6, py: 2 }}
                                >
                                    <Typography sx={{ fontSize: '1.2em' }} color="grey.100">
                                        Start Free Trial
                                    </Typography>
                                </Button>
                            </Link>
                            <Link to="/#contact">
                                <Button
                                    variant="outlined"
                                    size="large"
                                    sx={{ pl: 5, pr: 5, py: 2, borderColor: 'grey.500', color: 'grey.300' }}
                                >
                                    <Typography sx={{ fontSize: '1.2em' }}>Contact Us</Typography>
                                </Button>
                            </Link>
                        </Box>
                    </Grid>
                </Grid>
            </Box>
        </>
    );
}
